import React from "react";
import t from "../../../i18n";
import { minutesToFractional } from "../../../lib/time/minutes";
import type {
  ActivityIndexJSON,
  CategoryActivityType,
  CategoryProjectActivityType,
} from "../../../api/useActivities";
import {
  getEventStatusText,
  type DenormalizedEventStatus,
} from "../../Project/Events/utils/faultEventStatus";
import type { Translation } from "../../../api/useTranslations";
import type { Reparability } from "../../../api/Reparability";
import type { PriorityFaultEvent } from "diagnostics-domain/faultEvent";
import type { CustomTranslations } from "../../../i18n/types";
import { getEventPriorityText } from "../../Project/Events/utils/event";

const fr = {
  "action.confirm": "Confirmer",
  "activity.analysis.manual_failure_changed.analyses_added.subtitle"({ count }: { count: number }) {
    return (
      <>
        Ajout sur {count} {count < 2 ? "analyse" : "analyses"}
      </>
    );
  },
  "activity.analysis.manual_failure_changed.analyses_deleted.subtitle"({
    count,
  }: {
    count: number;
  }) {
    return (
      <>
        Suppression sur {count} {count < 2 ? "analyse" : "analyses"}
      </>
    );
  },
  "activity.analysis.manual_failure_changed.analyses_edited_impacts.subtitle"({
    count,
    impacts,
  }: {
    count: number;
    impacts: { name: string; previous: number; current: number }[];
  }) {
    return (
      <>
        {impacts.map(impact => (
          <>
            Modification de l{`'`}impact sur {impact.name} de{" "}
            {impact.previous === 0 ? "0" : `${impact.previous}`} à{" "}
            {impact.current === 0 ? "0" : `${impact.current}`} sur {count}{" "}
            {count < 2 ? "analyse" : "analyses"}
          </>
        ))}
      </>
    );
  },
  "activity.analysis.manual_failure_changed.analyses_edited_reparability.subtitle"({
    count,
    reparability,
    translation,
  }: {
    count: number;
    reparability: { previous: Reparability | null; current: Reparability | null };
    translation: {
      FR: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
      EN: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
    };
  }) {
    return (
      <>
        Modification de la réparabilité{" "}
        {reparability.previous === null
          ? ""
          : ` de ${
              reparability.previous
                ? fr["reparability"](reparability.previous, translation)
                : t("reparability.none")
            }`}{" "}
        en{" "}
        {reparability.current
          ? fr["reparability"](reparability.current, translation)
          : t("reparability.none")}{" "}
        sur {count} {count < 2 ? "analyse" : "analyses"}
      </>
    );
  },
  "activity.analysis.manual_failure_changed.title"({
    authorName,
    globalImpactedAnalysesCount,
    failureLink,
  }: {
    authorName: React.ReactNode;
    globalImpactedAnalysesCount: number;
    failureLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a édité {failureLink} sur {globalImpactedAnalysesCount}
        {globalImpactedAnalysesCount < 2 ? " analyse" : " analyses"}
      </>
    );
  },
  "activity.badge.setup.deleted": "Paramétrage supprimé",
  "activity.badge.setup.setting": "paramétrage",
  "activity.common.empty_field": "champ vide",
  "activity.description.cross_filters_deleted.title": ({
    authorName,
  }: {
    authorName: React.ReactNode;
  }) => <>{authorName} a supprimé les filtres croisés</>,
  "activity.description.cross_filters_imported.title": ({
    authorName,
  }: {
    authorName: React.ReactNode;
  }) => <>{authorName} a importé de nouveaux filtres croisés</>,
  "activity.description.live_data_flow_management.title": ({
    authorName,
    active,
  }: {
    authorName: React.ReactNode;
    active: boolean;
  }) => {
    if (active) return <>{authorName} a activé le flux temps réel</>;
    return <>{authorName} a mis en pause le flux temps réel</>;
  },
  "activity.event.badge.manual": "Manuel",
  "activity.manual_failure"({ count }: { count: number }) {
    return count === 1 ? `1  défaillance non modélisée` : `${count} défaillances non modélisées`;
  },
  "activity.manual_fault_event_created.title": ({
    authorName,
    eventLink,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
  }) => (
    <>
      {authorName} a créé l{`'`}événement manuel {eventLink}
    </>
  ),
  "activity.manual_fault_event_dashboard_created.title": ({
    authorName,
    eventLink,
    dashboardLink,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
    dashboardLink: React.ReactNode;
  }) => (
    <>
      {authorName} a lié l{`'`}événement manuel {eventLink} au tableau de bord {dashboardLink}
    </>
  ),
  "activity.manual_fault_event_dashboard_deleted.title": ({
    authorName,
    eventLink,
    dashboardLink,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
    dashboardLink: React.ReactNode;
  }) => (
    <>
      {authorName} a supprimé le lien entre l{`'`}événement manuel {eventLink} et le tableau de bord{" "}
      {dashboardLink}
    </>
  ),
  "activity.manual_fault_event_deleted.title": ({
    authorName,
    eventLink,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
  }) => (
    <>
      {authorName} a supprimé l{`'`}événement manuel {eventLink}
    </>
  ),
  "activity.manual_fault_event_status_updated.details.closed_at": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Date de fin : ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.details.description": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Description : ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.details.identified_action": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Action identifiée : ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.details.identified_action_set": ({
    current,
  }: {
    current: string;
  }) => `Action identifiée : ${current}`,
  "activity.manual_fault_event_status_updated.details.name": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Nom : ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.details.priority": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Priorité : ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.details.reparability": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Réparabilité : ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.details.status": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Statut : ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.title": ({
    authorName,
    eventLink,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
  }) => (
    <>
      {authorName} a modifié l{`'`}événement manuel {eventLink}
    </>
  ),
  "activity.manual_fault_event_time_window_created.title": ({
    authorName,
    eventLink,
    startDate,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
    startDate: string;
  }) => (
    <>
      {authorName} a ajouté une nouvelle période à partir du {startDate} sur l{`'`}événement manuel{" "}
      {eventLink}
    </>
  ),
  "activity.manual_fault_event_time_window_deleted.title": ({
    authorName,
    eventLink,
    startDate,
    endDate,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
    startDate: string;
    endDate: string | null;
  }) => (
    <>
      {authorName} a supprimé la période{" "}
      {endDate ? `du ${startDate} au ${endDate}` : `commençant le ${startDate}`} de l{`'`}événement
      manuel {eventLink}
    </>
  ),
  "activity.manual_fault_event_time_window_updated.details.impact": ({
    indicatorName,
    value,
    unit,
  }: {
    indicatorName: string;
    value: number;
    unit: string | null;
  }) => (
    <>
      Impact sur {indicatorName} : {value}
      {unit ? ` ${unit}` : ""}
    </>
  ),
  "activity.manual_fault_event_time_window_updated.details.start": ({
    startDate,
  }: {
    startDate: string;
  }) => <>Date de début : {startDate}</>,
  "activity.manual_fault_event_time_window_updated.title": ({
    authorName,
    eventLink,
    startDate,
    endDate,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
    startDate: string;
    endDate: string | null;
  }) => (
    <>
      {authorName} a modifié la période{" "}
      {endDate ? `du ${startDate} au ${endDate}` : `commençant le ${startDate}`} dans l{`'`}
      événement manuel {eventLink}
    </>
  ),
  "activity.project_duplicated.title"({
    authorName,
    targetProjectLink,
  }: {
    authorName: React.ReactNode;
    targetProjectLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a dupliqué le projet vers {targetProjectLink}
      </>
    );
  },
  "activity.project_duplicated_from.title"({
    authorName,
    sourceProjectLink,
  }: {
    authorName: React.ReactNode;
    sourceProjectLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a dupliqué {sourceProjectLink} vers ce projet
      </>
    );
  },
  "activity.project_manual_failure_created.title"({
    authorName,
    failureName,
  }: {
    authorName: React.ReactNode;
    failureName: string;
  }) {
    return (
      <>
        {authorName} a créé la défaillance non modélisée {failureName}
      </>
    );
  },

  "activity.project_manual_failure_deleted.title"({
    authorName,
    failureName,
  }: {
    authorName: React.ReactNode;
    failureName: string;
  }) {
    return (
      <>
        {authorName} a supprimé la défaillance non modélisée {failureName}
      </>
    );
  },
  "activity.selectFilter.analysis.manual_failure.changed":
    "Analyse - Gestion des défaillances non modélisées",
  "activity.selectFilter.failure_reintegrated": "Analyse - Réintégration de défaillance",
  "activity.selectFilter.manual_fault_event.created": "Événement manuel - Création",
  "activity.selectFilter.manual_fault_event.dashboard_created":
    "Événement manuel - Lien tableau de bord - Création",
  "activity.selectFilter.manual_fault_event.dashboard_deleted":
    "Événement manuel - Lien tableau de bord - Suppression",
  "activity.selectFilter.manual_fault_event.deleted": "Événement manuel - Suppression",
  "activity.selectFilter.manual_fault_event.status_updated": "Événement manuel - Modification",
  "activity.selectFilter.manual_fault_event.time_window_created":
    "Événement manuel - Période - Ajout",
  "activity.selectFilter.manual_fault_event.time_window_deleted":
    "Événement manuel - Période - Suppression",
  "activity.selectFilter.manual_fault_event.time_window_updated":
    "Événement manuel - Période - Modification",
  "activity.selectFilter.project_manual_failure.created":
    "Projet - Création de défaillances non modélisées",
  "activity.selectFilter.project_manual_failure.deleted":
    "Projet - Suppression de défaillances non modélisées",
  "activity.selectFilter.project_manual_failure_metadata.changed":
    "Projet - Modification de défaillances non modélisées",
  "activity.setup_diagnosis_algorithm_changed.details": ({
    prevPresetKey,
    currPresetKey,
    prevParameters,
    currParameters,
  }: {
    prevPresetKey?: string;
    currPresetKey?: string;
    prevParameters?: {
      key: string;
      value: string | number | boolean;
    }[];
    currParameters?: {
      key: string;
      value: string | number | boolean;
    }[];
  }) => (
    <>
      {prevPresetKey && currPresetKey && (
        <p>
          Modification de la version de {prevPresetKey} à {currPresetKey}
        </p>
      )}
      {!prevParameters && currParameters && (
        <>
          {currParameters.map(m => {
            if (typeof m.value === "boolean")
              return (
                <p key={m.key}>
                  {m.value === true ? "Sélection" : "Déselection"} du paramètre {m.key}
                </p>
              );
            return (
              <p key={m.key}>
                Déclaration d&apos;un paramètre {m.key} à {m.value}
              </p>
            );
          })}
        </>
      )}
      {prevParameters && currParameters && (
        <>
          {prevParameters.map(prev => {
            const currValue = currParameters.find(f => f.key === prev.key)?.value;
            if (typeof prev.value === "boolean")
              return (
                <p key={prev.key}>
                  Modification du paramètre {prev.key} de{" "}
                  {prev.value ? "sélectionné" : "déselectionné"} à{" "}
                  {currValue === undefined
                    ? undefined
                    : currValue
                      ? "sélectionné"
                      : "déselectionné"}
                </p>
              );
            return (
              <p key={prev.key}>
                Modification du paramètre {prev.key} de {prev.value} à{" "}
                {currParameters.find(f => f.key === prev.key)?.value}
              </p>
            );
          })}
        </>
      )}
    </>
  ),
  "activity.setup_diagnosis_algorithm_changed.details.edited_parameter": (
    key: string,
    prevValue: string,
    currValue: string,
  ) => (
    <>
      Modification du paramètre {key} de {prevValue} à {currValue}
    </>
  ),
  "activity.setup_diagnosis_algorithm_changed.details.edited_preset": (
    presetOld: string,
    presetNew: string,
  ) => (
    <>
      Modification de la version de {presetOld} à {presetNew}
    </>
  ),
  "activity.setup_diagnosis_algorithm_changed.details.new_parameter": (
    key: string,
    value: number | boolean,
  ) => {
    if (typeof value === "boolean")
      return (
        <>
          {value === true ? "Sélection" : "Déselection"} du paramètre {key}
        </>
      );
    return (
      <>
        Déclaration d&apos;un paramètre {key} à {value}
      </>
    );
  },
  "activity.source_duplicated.range"({ from, to }: { from: string | null; to: string | null }) {
    if (from && to)
      return (
        <>
          {" "}
          de {from} à {to}
        </>
      );
    if (from) return <> à partir du {from}</>;
    return <> jusqu’au {to}</>;
  },
  "activity.source_duplicated.title"({
    originSourceId,
    targetSourceId,
    authorName,
  }: {
    originSourceId: string;
    targetSourceId: string;
    authorName: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a dupliqué la source de données {originSourceId} vers la source de données{" "}
        {targetSourceId}
      </>
    );
  },
  "analysis.publish.title": "Publication",
  "analysis.unpublish.title": "Dépublication",
  "analysis_activated.title"({
    authorName,
    nbAnalysis,
  }: {
    authorName: React.ReactNode;
    nbAnalysis: number;
  }) {
    return (
      <>
        {authorName} a créé {nbAnalysis} {`${nbAnalysis > 1 ? "analyses" : "analyse"}`}
      </>
    );
  },
  "analysis_activation_filters_deleted.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} a supprimé les filtres d&apos;analyse</>;
  },
  "analysis_activation_filters_replaced.title"({
    authorName,
  }: // settingsLink,
  {
    authorName: React.ReactNode;
    // settingsLink: React.ReactNode;
  }) {
    return <>{authorName} a importé de nouveaux filtres d&apos;analyse</>;
  },
  "analysis_activation_frequency_changed.title"({
    authorName,
    frequency,
  }: {
    authorName: React.ReactNode;
    frequency: number;
  }) {
    const splittedInterval = minutesToFractional(frequency);

    if (frequency === 0)
      return <>{authorName} a retiré la contrainte sur l’intervalle entre deux analyses</>;

    return (
      <>
        {authorName} a défini l&apos;intervalle entre deux analyses à{" "}
        {t("project.settings.analyses.frequency_value", splittedInterval)}
      </>
    );
  },
  "analysis_activation_reset.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} a relancé la sélection d&apos;analyses</>;
  },
  "analysis_deactivated.title"({
    authorName,
    nbAnalysis,
  }: {
    authorName: React.ReactNode;
    nbAnalysis: number;
  }) {
    return (
      <>
        {authorName} a supprimé {nbAnalysis} {`analyse${nbAnalysis > 1 ? "s" : ""}`}
      </>
    );
  },
  analysis_publication_changed: "Analyse - Modification de statut de publication",
  "analysis_publication_changed.title"({
    authorName,
    publishedCount,
    unpublishedCount,
  }: {
    authorName: React.ReactNode;
    publishedCount: number;
    unpublishedCount: number;
  }) {
    return (
      <>
        {authorName} a modifié la publication de {publishedCount + unpublishedCount}{" "}
        {publishedCount + unpublishedCount < 2 ? "analyse" : "analyses"}
      </>
    );
  },
  "category.project.type"(type: "EQUIPMENT" | "SUSPECTED_ELEMENT"): string {
    switch (type) {
      case "EQUIPMENT":
        return "équipements";
      case "SUSPECTED_ELEMENT":
        return "éléments suspectés";
      default:
        throw new Error(type satisfies never);
    }
  },
  "category.type"(type: CategoryActivityType, translation: Translation): string {
    switch (type) {
      case "PROJECT_TYPE":
        return translation.FR.TYPE;
      case "PROJECT_SITE":
        return "sites";
      case "FAILURE_CATEGORY":
        return "catégories de défaillances";
      case "DEGRADATION_TYPE":
        return "types de dégradation";
      case "EQUIPMENT_TYPE":
        return "types d'équipement";
      case "SYSTEM":
        return "systèmes";
      default:
        throw new Error(type satisfies never);
    }
  },
  "category_changed.title"({
    authorName,
    previousName,
    currentName,
    type,
    translation,
  }: {
    authorName: React.ReactNode;
    previousName: string;
    currentName: string;
    type: CategoryActivityType;
    translation: Translation;
  }) {
    return (
      <>
        {authorName} a renommé la catégorie {previousName} en {currentName} dans la liste des{" "}
        {fr["category.type"](type, translation)}
      </>
    );
  },
  "category_created.title"({
    authorName,
    categoryName,
    type,
    translation,
  }: {
    authorName: React.ReactNode;
    categoryName: string;
    type: CategoryActivityType;
    translation: Translation;
  }) {
    return (
      <>
        {authorName} a créé la catégorie {categoryName} dans la liste des{" "}
        {fr["category.type"](type, translation)}
      </>
    );
  },
  "category_deleted.title"({
    authorName,
    categoryName,
    type,
    translation,
  }: {
    authorName: React.ReactNode;
    categoryName: string;
    type: CategoryActivityType;
    translation: Translation;
  }) {
    return (
      <>
        {authorName} a supprimé la catégorie {categoryName} dans la liste des{" "}
        {fr["category.type"](type, translation)}
      </>
    );
  },
  comment: "Commentaire : ",
  "comment_changed.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} a modifié un commentaire</>;
  },
  "comment_created.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} a créé un commentaire</>;
  },
  "comment_dashboard_created.title"(
    {
      authorName,
      dashboardLink,
    }: {
      authorName: React.ReactNode;
      dashboardLink: React.ReactNode;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} a modifié le contenu du bloc de texte éditable dans le{" "}
        {translations.FR.DASHBOARD.toLowerCase()} {dashboardLink}
      </>
    );
  },
  "comment_deleted.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} a supprimé un commentaire</>;
  },
  "comment_edited.linked": ({
    analysisDate,
    faultEventName,
  }: {
    analysisDate: string | null;
    faultEventName: string | null;
  }) => {
    if (analysisDate !== null && faultEventName !== null) {
      return `Commentaire lié à l'analyse ${analysisDate} et à l'évènement ${faultEventName}`;
    } else if (analysisDate === null) {
      return `Commentaire lié à l'évènement ${faultEventName}`;
    } else {
      return `Commentaire lié à l'analyse ${analysisDate}`;
    }
  },
  comment_linked({
    analysisDate,
    faultEventName,
    dashboardName,
    translations: _translations, // required in the EN version, but ignored in the FR version
  }: {
    analysisDate: string | null;
    faultEventName: string | null;
    dashboardName: string | null;
    translations: CustomTranslations;
  }) {
    if (analysisDate !== null && faultEventName !== null) {
      return `Commentaire lié à l'analyse ${analysisDate} et à l'évènement ${faultEventName} : `;
    } else if (analysisDate !== null) {
      return `Commentaire lié à l'analyse ${analysisDate} : `;
    } else if (dashboardName === null) {
      return `Commentaire lié à l'évènement ${faultEventName} : `;
    } else {
      return `Commentaire lié au {translations.FR.DASHBOARD.toLowerCase()} ${dashboardName} : `;
    }
  },
  comment_new: "Nouveau commentaire : ",
  comment_old: "Ancien commentaire : ",
  "commons.analysis_title.group_and_label": ({ group, label }: { group: string; label: string }) =>
    `Cycle ${group} - Essai ${label}`,
  "dashboard_changed.title"(
    {
      authorName,
      dashboardLink,
    }: {
      authorName: React.ReactNode;
      dashboardLink: React.ReactNode;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} a modifié le {translations.FR.DASHBOARD.toLowerCase()} {dashboardLink}
      </>
    );
  },
  dashboard_comment_new: "Nouveau contenu : ",
  dashboard_comment_old: "Précédent contenu : ",
  "dashboard_created.title"(
    {
      authorName,
      dashboardLink,
    }: {
      authorName: React.ReactNode;
      dashboardLink: React.ReactNode;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} a créé le {translations.FR.DASHBOARD.toLowerCase()} {dashboardLink}
      </>
    );
  },
  "dashboard_deleted.title"(
    {
      authorName,
      dashboardName,
    }: {
      authorName: React.ReactNode;
      dashboardName: string;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} a supprimé le {translations.FR.DASHBOARD.toLowerCase()} {dashboardName}
      </>
    );
  },
  "dashboard_duplicated.title"(
    {
      authorName,
      sourceDashboardLink,
      targetDashboardLink,
    }: {
      authorName: React.ReactNode;
      sourceDashboardLink: React.ReactNode;
      targetDashboardLink: React.ReactNode;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} a dupliqué le {translations.FR.DASHBOARD.toLowerCase()} {sourceDashboardLink}{" "}
        vers le {translations.FR.DASHBOARD.toLowerCase()} {targetDashboardLink}
      </>
    );
  },
  "dashboard_publication_changed.title"(
    {
      authorName,
      dashboardLink,
      published,
      readonly,
    }: {
      authorName: React.ReactNode;
      dashboardLink: React.ReactNode;
      published: boolean;
      readonly: boolean;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} a {published ? "partagé" : "rendu"} le{" "}
        {translations.FR.DASHBOARD.toLowerCase()} {dashboardLink}{" "}
        {published ? (readonly ? "en lecture seule " : "") : ""}
        {published ? "" : "privé"}
      </>
    );
  },
  "diagnosis_ended.title"({
    analysesLink,
    analysesCount,
  }: {
    analysesLink: React.ReactNode;
    analysesCount: number;
  }): React.ReactNode {
    if (analysesCount === 1) {
      return <>{analysesLink} diagnostic réalisé</>;
    }
    return <>{analysesLink} diagnostics réalisés</>;
  },
  failure({ count }: { count: number }) {
    return `${count} défaillance${count < 2 ? "" : "s"}`;
  },
  "failure_reparability_changed.title"({
    authorName,
    failureLink,
    reparability,
    startDate,
    endDate,
    translation,
  }: {
    authorName: React.ReactNode;
    failureLink: React.ReactNode;
    reparability: Reparability | null;
    startDate: string;
    endDate: string;
    translation: {
      FR: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
      EN: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
    };
  }) {
    const verb = reparability
      ? `a déclaré la réparabilité ${fr["reparability"](reparability, translation)} sur`
      : "a supprimé la réparabilité sur";

    if (startDate === endDate) {
      return (
        <>
          {authorName} {verb} {failureLink} en date du {startDate}
        </>
      );
    }
    return (
      <>
        {authorName} {verb} {failureLink} comprises entre le {startDate} et le {endDate}
      </>
    );
  },
  "failure_status_changed.title"({
    authorName,
    failureLink,
    status,
    startDate,
    endDate,
  }: {
    authorName: React.ReactNode;
    failureLink: React.ReactNode;
    status:
      | "CONFIRMED"
      | "REJECTED"
      | "SUSPECTED"
      | "FORCED_FALSE"
      | "REJECTED"
      | "LEGACY_REJECTED"
      | null;
    startDate: string;
    endDate: string;
  }) {
    const verb =
      status === "CONFIRMED"
        ? "a confirmé"
        : status === "REJECTED" || status === "FORCED_FALSE" || status === "LEGACY_REJECTED"
          ? "a déclaré au statut fausse détection"
          : status === null
            ? "a déclaré au status à analyser"
            : "a déclaré au statut suspectée";

    const endVerb = status === "REJECTED" ? "et a relancé un diagnostic" : "";

    if (startDate === endDate) {
      return (
        <>
          {authorName} {verb} {failureLink} en date du {startDate} {endVerb}
        </>
      );
    }
    return (
      <>
        {authorName} {verb} {failureLink} comprises entre le {startDate} et le {endDate} {endVerb}
      </>
    );
  },
  "fault_event_dashboard_created.title"(
    {
      authorName,
      faultEventLink,
      dashboardLink,
    }: {
      authorName: React.ReactNode;
      faultEventLink: React.ReactNode;
      dashboardLink: React.ReactNode;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} a ajouté un lien vers le {translations.FR.DASHBOARD.toLowerCase()}{" "}
        {dashboardLink} dans l&apos;événement {faultEventLink}
      </>
    );
  },
  "fault_event_dashboard_deleted.title"(
    {
      authorName,
      faultEventLink,
      dashboardLink,
    }: {
      authorName: React.ReactNode;
      faultEventLink: React.ReactNode;
      dashboardLink: React.ReactNode;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} a retiré le lien vers le {translations.FR.DASHBOARD.toLowerCase()}{" "}
        {dashboardLink} dans l&apos;événement {faultEventLink}
      </>
    );
  },
  "fault_event_diagnoses_status_changed.title": ({
    authorName,
    properties,
    faultEventLink,
  }: {
    authorName: React.ReactNode;
    properties: ActivityIndexJSON["FAULT_EVENT_DIAGNOSES_STATUS_CHANGED"]["properties"];
    faultEventLink: React.ReactNode;
  }) => {
    const status = properties.newDiagnosesStatus.current;

    let verb: string;
    switch (status) {
      case "CONFIRMED": {
        verb = "Confirmé";
        break;
      }
      case "SUSPECTED": {
        verb = "Suspecté";
        break;
      }
      case "FORCED_FALSE":
      case "LEGACY_REJECTED": {
        verb = "Fausse détection (sans le relancement de diagnostic)";
        break;
      }
      case "REJECTED": {
        verb = "Fausse détection (avec le relancement de diagnostic)";
        break;
      }
      case null: {
        verb = "À analyser";
        break;
      }
      default:
        throw new Error(status satisfies never);
    }

    return (
      <>
        {authorName} a modifié le statut appliqué aux nouvelles détections pour l’événement{" "}
        {faultEventLink} en &quot;{verb}&quot;
      </>
    );
  },
  "fault_event_merge.title"({
    authorName,
    mergeEventLink,
    targetEventName,
    sourceEventName,
  }: {
    authorName: React.ReactNode;
    mergeEventLink: React.ReactNode;
    targetEventName: string;
    sourceEventName: string;
  }) {
    return (
      <>
        {authorName} a fusionné les événements {targetEventName} et {sourceEventName} en{" "}
        {mergeEventLink}
      </>
    );
  },
  "fault_event_name_changed.title"({
    authorName,
    properties,
    faultEventLink,
  }: {
    authorName: React.ReactNode;
    properties: ActivityIndexJSON["FAULT_EVENT_NAME_CHANGED"]["properties"];
    faultEventLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a renommé l’événement {properties.name.previous} en {faultEventLink}
      </>
    );
  },
  "fault_event_priority_changed.details"({
    previous,
    current,
  }: {
    previous: PriorityFaultEvent | null;
    current: PriorityFaultEvent | null;
  }) {
    const calculatedValue = "la valeur calculée";
    const previousValue = previous === null ? calculatedValue : getEventPriorityText(previous);
    const currentValue = current === null ? calculatedValue : getEventPriorityText(current);
    return `Priorité modifiée de ${previousValue} à ${currentValue}`;
  },
  "fault_event_priority_changed.title"({
    authorName,
    faultEventLink,
  }: {
    authorName: React.ReactNode;
    faultEventLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a modifié la priorité de l’événement {faultEventLink}
      </>
    );
  },
  "fault_event_split.title"({
    authorName,
    originalEventLink,
    newSplitEventLink,
  }: {
    authorName: React.ReactNode;
    originalEventLink: React.ReactNode;
    newSplitEventLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a créé l’événement {newSplitEventLink} sur la base de l’événement{" "}
        {originalEventLink}
      </>
    );
  },
  "fault_event_status_changed.action": ({ action }: { action: string }) =>
    `Actions identifiées : ${action}`,
  "fault_event_status_changed.detection": ({ detection }: { detection: boolean }) =>
    `Type de détection : ${detection ? "vraie détection" : "fausse détection"}`,
  "fault_event_status_changed.priority": (priority: PriorityFaultEvent) =>
    `Priorité : ${getEventPriorityText(priority)}`,
  "fault_event_status_changed.reparability": ({
    reparability,
    translation,
  }: {
    reparability: Reparability;
    translation: Translation;
  }) => `Réparabilité : ${fr["reparability"](reparability, translation)}`,
  "fault_event_status_changed.status": ({
    previous,
    current,
  }: {
    previous: DenormalizedEventStatus;
    current: DenormalizedEventStatus;
  }) => `Statut modifié de "${getEventStatusText(previous)}" à "${getEventStatusText(current)}"`,
  "fault_event_status_changed.title"({
    authorName,
    faultEventLink,
  }: {
    authorName: React.ReactNode;
    faultEventLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a modifié le statut de l’événement {faultEventLink}
      </>
    );
  },
  "fault_reintegrated.title": ({
    authorName,
    failureLink,
    startDate,
    endDate,
  }: {
    authorName: React.ReactNode;
    failureLink: React.ReactNode;
    startDate: string;
    endDate: string;
  }) => {
    if (startDate === endDate) {
      return (
        <>
          {authorName} a réintégré dans le diagnostic {failureLink} en date du {startDate}
        </>
      );
    }
    return (
      <>
        {authorName} a réintégré dans le diagnostic {failureLink} comprises entre le {startDate} et
        le {endDate}
      </>
    );
  },
  "formula_quantity_created.title"({
    authorName,
    quantityName,
    formula,
  }: {
    authorName: React.ReactNode;
    quantityName: string;
    formula: string;
  }) {
    return (
      <>
        {authorName} a créé la grandeur calculée {quantityName}, avec la formule {formula}
      </>
    );
  },
  "lexical.quantities": "grandeurs",
  "lexical.settings": "réglages",
  measure({ count }: { count: number }) {
    return `${count} mesure${count < 2 ? "" : "s"}`;
  },
  "measures_validity_changed.title"({
    authorName,
    action,
    measuresLink,
    firstDate,
    lastDate,
  }: {
    authorName: React.ReactNode;
    action: "VALIDATION" | "INVALIDATION";
    measuresLink: React.ReactNode;
    firstDate: string;
    lastDate: string;
  }) {
    const verb = action === "VALIDATION" ? "a validé" : "a invalidé";
    if (firstDate === lastDate)
      return (
        <>
          {authorName} {verb} {measuresLink} en date du {firstDate}
        </>
      );
    return (
      <>
        {authorName} {verb} {measuresLink} entre le {firstDate} et le {lastDate}
      </>
    );
  },
  "outdated.title"({ authorName, action }: { authorName: React.ReactNode; action: string }) {
    return (
      <>
        {authorName} a effectué une action &quot;{action}&quot;
      </>
    );
  },
  "project_category_changed.title"({
    authorName,
    previousName,
    currentName,
    type,
  }: {
    authorName: React.ReactNode;
    previousName: string;
    currentName: string;
    type: CategoryProjectActivityType;
  }) {
    return (
      <>
        {authorName} a renommé la catégorie {previousName} en {currentName} dans la liste des{" "}
        {fr["category.project.type"](type)}
      </>
    );
  },
  "project_category_created.title"({
    authorName,
    categoryName,
    type,
  }: {
    authorName: React.ReactNode;
    categoryName: string;
    type: CategoryProjectActivityType;
  }) {
    return (
      <>
        {authorName} a créé la catégorie {categoryName} dans la liste des{" "}
        {fr["category.project.type"](type)}
      </>
    );
  },
  "project_category_deleted.title"({
    authorName,
    categoryName,
    type,
  }: {
    authorName: React.ReactNode;
    categoryName: string;
    type: CategoryProjectActivityType;
  }) {
    return (
      <>
        {authorName} a supprimé la catégorie {categoryName} dans la liste des{" "}
        {fr["category.project.type"](type)}
      </>
    );
  },
  "project_changed.details.description"({
    currentDescription,
    previousDescription,
  }: {
    currentDescription: string;
    previousDescription: string;
  }) {
    return `La description "${previousDescription}" a été changé en "${currentDescription}"`;
  },
  "project_changed.details.description.add"({
    currentDescription,
  }: {
    currentDescription: string;
  }) {
    return `La description "${currentDescription}" a été ajoutée`;
  },
  "project_changed.details.description.deleted": "La description a été supprimée",
  "project_changed.details.description.modified"({
    currentDescription,
    previousDescription,
  }: {
    currentDescription: string;
    previousDescription: string;
  }) {
    return `La description "${previousDescription}" a été changé en "${currentDescription}"`;
  },
  "project_changed.details.name"({
    previousName,
    currentName,
  }: {
    previousName: string;
    currentName: string;
  }) {
    return `Le projet ${previousName} a été renommé en ${currentName}`;
  },
  "project_changed.details.production": `Le projet a été mis en production`,
  "project_changed.details.production.draft": `Le projet a été passé en recherche`,
  "project_changed.details.site.add"({ currentSiteName }: { currentSiteName: string }) {
    return `Le site "${currentSiteName}" a été ajouté`;
  },
  "project_changed.details.site.deleted": "Le site a été supprimé",
  "project_changed.details.site.modified"({
    currentSiteName,
    previousSiteName,
  }: {
    currentSiteName: string;
    previousSiteName: string;
  }) {
    return `Le site "${previousSiteName}" a été changé en "${currentSiteName}"`;
  },
  "project_changed.details.sourceId"({ previous, current }: { previous: string; current: string }) {
    return `La source de données a été modifiée de ${previous} à ${current}`;
  },
  "project_changed.details.timezone"({
    previousTimezone,
    currentTimezone,
  }: {
    previousTimezone: string;
    currentTimezone: string;
  }) {
    return `Le fuseau horaire a été changé de ${previousTimezone} en ${currentTimezone}`;
  },
  "project_changed.details.type.add"({
    currentTypeName,
    translation,
  }: {
    currentTypeName: string;
    translation: {
      FR: {
        TYPE: string;
      };
      EN: {
        TYPE: string;
      };
    };
  }) {
    return `Le ${translation.FR.TYPE} "${currentTypeName}" a été ajouté`;
  },
  "project_changed.details.type.deleted"({
    translation,
  }: {
    translation: {
      FR: {
        TYPE: string;
      };
      EN: {
        TYPE: string;
      };
    };
  }) {
    return `Le ${translation.FR.TYPE}  a été supprimé`;
  },
  "project_changed.details.type.modified"({
    currentTypeName,
    previousTypeName,
    translation,
  }: {
    currentTypeName: string;
    previousTypeName: string;
    translation: {
      FR: {
        TYPE: string;
      };
      EN: {
        TYPE: string;
      };
    };
  }) {
    return `Le ${translation.FR.TYPE} "${previousTypeName}" a été changé en "${currentTypeName}"`;
  },
  "project_changed.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} a modifié les attributs du projet</>;
  },
  "project_created.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} a créé le projet</>;
  },
  "project_deleted.title"({
    authorName,
    projectName,
  }: {
    authorName: React.ReactNode;
    projectName: string;
  }) {
    return (
      <>
        {authorName} a supprimé le projet {projectName}
      </>
    );
  },
  "project_manual_failure_metadata_changed.title"({
    authorName,
    failureName,
  }: {
    authorName: React.ReactNode;
    failureName: string;
  }) {
    return (
      <>
        {authorName} a modifié les métadonnées de la défaillance non modélisée {failureName}
      </>
    );
  },
  "projects.modal.create_or_edit.input.type": (translation: Translation) => translation.FR.TYPE,
  "qois_changed.title"({
    authorName,
    qoiNames,
  }: {
    authorName: React.ReactNode;
    qoiNames: string[];
  }) {
    return qoiNames.length === 0 ? (
      <>{authorName} a supprimé les indicateurs de performance</>
    ) : qoiNames.length === 1 ? (
      <>
        {authorName} a changé la liste des indicateurs de performance : le nouvel indicateur de
        performance est {qoiNames[0]}
      </>
    ) : (
      <>
        {authorName} a changé la liste des indicateurs de performance : les nouveaux indicateurs de
        performance sont {qoiNames.join(", ")}
      </>
    );
  },
  "quantites_changed.details.description"({
    previousDescription,
    currentDescription,
  }: {
    previousDescription: string | null;
    currentDescription: string | null;
  }) {
    return `Modification de la description ${
      currentDescription === null && previousDescription !== null
        ? ` à un champ vide`
        : previousDescription === null && currentDescription !== null
          ? ` avec un ajout à ${currentDescription}`
          : ` de ${previousDescription} à ${currentDescription}`
    }`;
  },
  "quantites_changed.details.equipmentType"({
    currentEquipmentTypeName,
    previousEquipmentTypeName,
  }: {
    currentEquipmentTypeName: string | null;
    previousEquipmentTypeName: string | null;
  }) {
    return `Modification du type d'équipement ${
      currentEquipmentTypeName === null && previousEquipmentTypeName !== null
        ? ` à un champ vide`
        : previousEquipmentTypeName === null && currentEquipmentTypeName !== null
          ? ` avec un ajout à ${currentEquipmentTypeName}`
          : ` de ${previousEquipmentTypeName} à ${currentEquipmentTypeName}`
    }`;
  },
  "quantites_changed.details.equipments"({
    currentEquipments,
    previousEquipments,
  }: {
    currentEquipments: string[];
    previousEquipments: string[];
  }) {
    return `Modification des équipements ${
      currentEquipments.length === 0 && previousEquipments.length > 0
        ? ` à un champ vide`
        : previousEquipments.length === 0 && currentEquipments.length > 0
          ? ` avec un ajout à ${currentEquipments.join(",")}`
          : ` de ${previousEquipments.join(",")} à ${currentEquipments.join(",")}`
    }`;
  },
  "quantites_changed.details.formula"({
    previousFormula,
    currentFormula,
  }: {
    previousFormula: string | null;
    currentFormula: string | null;
  }) {
    return `Modification de la formule ${
      currentFormula === null && previousFormula !== null
        ? ` à un champ vide`
        : previousFormula === null && currentFormula !== null
          ? ` avec un ajout à ${currentFormula}`
          : ` de ${previousFormula} à ${currentFormula}`
    }`;
  },
  "quantites_changed.details.formulaVersion.created"({
    startsAt,
    expression,
  }: {
    startsAt: string;
    expression: string;
  }) {
    return `Création d’une version de formule à partir du ${startsAt} - Formule : ${expression}`;
  },
  "quantites_changed.details.formulaVersion.deleted"({
    startsAt,
    expression,
  }: {
    startsAt: string;
    expression: string;
  }) {
    return `Suppression d’une version de formule à partir du ${startsAt} - Formule : ${expression}`;
  },
  "quantites_changed.details.formulaVersion.updated"({
    startsAt,
    expression,
  }: {
    startsAt: string;
    expression: string;
  }) {
    return `Modification d’une version de formule à partir du ${startsAt} - Formule : ${expression}`;
  },
  "quantites_changed.details.max"({
    currentMax,
    previousMax,
  }: {
    currentMax: number | null;
    previousMax: number | null;
  }) {
    return `Modification du maximum ${
      currentMax === null && previousMax !== null
        ? ` à un champ vide`
        : previousMax === null && currentMax !== null
          ? ` avec un ajout à ${currentMax}`
          : ` de ${previousMax} à ${currentMax}`
    }`;
  },
  "quantites_changed.details.maxstd"({
    currentMaxstd,
    previousMaxstd,
  }: {
    currentMaxstd: number | null;
    previousMaxstd: number | null;
  }) {
    return `Modification de l'écart-type max ${
      currentMaxstd === null && previousMaxstd !== null
        ? ` à un champ vide`
        : previousMaxstd === null && currentMaxstd !== null
          ? ` avec un ajout à ${currentMaxstd}`
          : ` de ${previousMaxstd} à ${currentMaxstd}`
    }`;
  },
  "quantites_changed.details.min"({
    currentMin,
    previousMin,
  }: {
    currentMin: number | null;
    previousMin: number | null;
  }) {
    return `Modification du minimum ${
      currentMin === null && previousMin !== null
        ? ` à un champ vide`
        : previousMin === null && currentMin !== null
          ? ` avec un ajout à ${currentMin}`
          : ` de ${previousMin} à ${currentMin}`
    }`;
  },
  "quantites_changed.details.name.modified"({
    currentName,
    previousName,
  }: {
    currentName: string | null;
    previousName: string | null;
  }) {
    return `Modification du nom de "${previousName}" à "${currentName}"`;
  },
  "quantites_changed.details.system"({
    currentSystemName,
    previousSystemName,
  }: {
    currentSystemName: string | null;
    previousSystemName: string | null;
  }) {
    return `Modification du système ${
      currentSystemName === null && previousSystemName !== null
        ? ` à un champ vide`
        : previousSystemName === null && currentSystemName !== null
          ? ` avec un ajout à ${currentSystemName}`
          : ` de ${previousSystemName} à ${currentSystemName}`
    }`;
  },
  "quantites_changed.details.unit"({
    previousUnit,
    currentUnit,
  }: {
    previousUnit: string | null;
    currentUnit: string | null;
  }) {
    return `Modification de l'unité ${
      currentUnit === null && previousUnit !== null
        ? ` à un champ vide`
        : previousUnit === null && currentUnit !== null
          ? ` avec un ajout à ${currentUnit}`
          : ` de ${previousUnit} à ${currentUnit}`
    }`;
  },
  "quantities_changed.title"({
    authorName,
    quantitiesLink,
    nbQuantities,
  }: {
    authorName: React.ReactNode;
    quantitiesLink: React.ReactNode;
    nbQuantities: number;
  }) {
    return (
      <>
        {authorName} a modifié {nbQuantities === 1 ? "une grandeur" : "des grandeurs"} dans la page{" "}
        {quantitiesLink}
      </>
    );
  },
  "quantities_deleted.title"({
    authorName,
    count,
  }: {
    authorName: React.ReactNode;
    count: number;
  }) {
    return (
      <>
        {authorName} a supprimé {count} grandeur{count > 1 ? "s" : ""}
      </>
    );
  },
  "quantities_replaced.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} a importé de nouvelles grandeurs</>;
  },
  related_analyses_deleted({ count }: { count: number }): string {
    return count < 2
      ? "L'analyse associée a été supprimée"
      : "Les analyses associées ont été supprimées";
  },
  reparability: (
    reparability: Reparability,
    translation: {
      FR: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
      EN: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
    },
  ) => {
    switch (reparability) {
      case "REPARABILITY_0":
        return translation.FR.REPARABILITY_0;
      case "REPARABILITY_1":
        return translation.FR.REPARABILITY_1;
      case "REPARABILITY_2":
        return translation.FR.REPARABILITY_2;
      default:
        throw new Error(reparability satisfies never);
    }
  },
  "selectFilter.analysis.manual_failure.changed":
    "Analyse - Gestion des défaillances non modélisées",
  "selectFilter.analysis_activated": "Analyse - Création d'analyses",
  "selectFilter.analysis_activation_filters_deleted":
    "Réglages - Suppression des filtres d'analyse",
  "selectFilter.analysis_activation_filters_replaced": "Réglages - Import des filtres d'analyse",
  "selectFilter.analysis_activation_frenquency_changed":
    "Réglages - Modification de l'intervalle entre deux analyses",
  "selectFilter.analysis_activation_reset": "Analyse - Réinitialisation du choix des analyses",
  "selectFilter.analysis_deactivated": "Analyse - Suppression d’analyses",
  "selectFilter.category_changed": "Catégorie - Modification",
  "selectFilter.category_created": "Catégorie - Création",
  "selectFilter.category_deleted": "Catégorie - Suppression",
  "selectFilter.comment_changed": "Commentaire - Modification",
  "selectFilter.comment_created": "Commentaire - Création",
  "selectFilter.comment_deleted": "Commentaire - Suppression",
  "selectFilter.cross_filters_deleted": "Grandeurs - Suppression de filtres croisés",
  "selectFilter.cross_filters_imported": "Grandeurs - Import de filtres croisés",
  "selectFilter.dashboard_changed": (translations: CustomTranslations) =>
    `${translations.FR.DASHBOARD} - Modification`,
  "selectFilter.dashboard_comment_created": (translations: CustomTranslations) =>
    `${translations.FR.DASHBOARD} - Modification du contenu du bloc Texte éditable`,
  "selectFilter.dashboard_created": (translations: CustomTranslations) =>
    `${translations.FR.DASHBOARD} - Création`,
  "selectFilter.dashboard_deleted": (translations: CustomTranslations) =>
    `${translations.FR.DASHBOARD} - Suppression`,
  "selectFilter.dashboard_duplicated": (translations: CustomTranslations) =>
    `${translations.FR.DASHBOARD} - Duplication`,
  "selectFilter.dashboard_publication_changed": (translations: CustomTranslations) =>
    `${translations.FR.DASHBOARD} - Modification du partage`,
  "selectFilter.diagnosis_ended": "Analyse - Diagnostic terminé",
  "selectFilter.failure.reparability.changed":
    "Analyse - Modification de réparabilité de défaillance",
  "selectFilter.failures_status_changed": "Analyse - Modification de statut de défaillance",
  "selectFilter.fault_event_dashboard_created": (translations: CustomTranslations) =>
    `Événement - Lien avec ${translations.FR.DASHBOARD.toLowerCase()} - Création`,
  "selectFilter.fault_event_dashboard_deleted": (translations: CustomTranslations) =>
    `Événement - Lien avec ${translations.FR.DASHBOARD.toLowerCase()} - Suppression`,
  "selectFilter.fault_event_diagnoses_status_changed":
    "Événement - Changement de statut appliqué aux nouvelles détections",
  "selectFilter.fault_event_merge": "Événement - Fusion d’événements",
  "selectFilter.fault_event_name_changed": "Événement - Modification de nom de l'évènement",
  "selectFilter.fault_event_priority_changed":
    "Événement - Modification de priorité de l'évènement",
  "selectFilter.fault_event_split": "Événement - Division d’événement",
  "selectFilter.fault_event_status_changed": "Événement - Modification de statut d'évènement",
  "selectFilter.formula_quantity_created": "Grandeur - Ajout de grandeur calculée",
  "selectFilter.live_data_flow_management": "Projet - Gestion du flux temps réel",
  "selectFilter.measures_validity_changed": "Analyse - Modification de validité de mesure",
  "selectFilter.project_category_changed": "Catégorie de projet - Modification",
  "selectFilter.project_category_created": "Catégorie de projet - Création",
  "selectFilter.project_category_deleted": "Catégorie de projet - Suppression",
  "selectFilter.project_changed": "Projet - Modification des attributs",
  "selectFilter.project_created": "Projet - Création",
  "selectFilter.project_deleted": "Projet - Suppression",
  "selectFilter.project_duplicated": "Projet - Duplication (projet source)",
  "selectFilter.project_duplicated_from": "Projet - Duplication (projet cible)",
  "selectFilter.project_manual_failure.created": "Projet - Création de défaillances non modélisées",
  "selectFilter.project_manual_failure_metadata.changed":
    "Projet - Modification de défaillances non modélisées",
  "selectFilter.qois_changed": "Réglages - Indicateur de performance",
  "selectFilter.quantities_changed": "Grandeur - Modification de grandeurs",
  "selectFilter.quantities_deleted": "Grandeur - Suppression de grandeurs",
  "selectFilter.quantities_replaced": "Grandeur - Import de grandeurs",
  "selectFilter.sensor_quantities_created": "Grandeur - Ajout de capteurs",
  "selectFilter.sensor_value_deleted": "Source - Suppression de données d'un capteur",
  "selectFilter.setup_changed": "Paramétrage - Modification des attributs",
  "selectFilter.setup_created": "Paramétrage - Création",
  "selectFilter.setup_deleted": "Paramétrage - Suppression",
  "selectFilter.setup_diagnosis_diagnosis_failures_changed":
    "Paramétrage - Modification de défaillances",
  "selectFilter.setup_diagnosis_diagnosis_failures_deleted":
    "Paramétrage - Suppression des défaillances",
  "selectFilter.setup_diagnosis_failures_metadata_changed":
    "Paramétrage - Modification de métadonnées de défaillances",
  "selectFilter.setup_diagnosis_failures_replaced": "Paramétrage - Import de défaillances",
  "selectFilter.setup_diagnosis_observable_changed": "Paramétrage - Modification d'observables",
  "selectFilter.setup_diagnosis_observable_deleted": "Paramétrage - Suppression des observables",
  "selectFilter.setup_diagnosis_observable_replaced": "Paramétrage - Import des observables",
  "selectFilter.setup_diagnosis_parameters_changed":
    "Paramétrage - Modification des paramètres de l'algorithme",
  "selectFilter.setup_diagnosis_preset_changed": "Paramétrage - Modification de l'algorithme",
  "selectFilter.setup_duplicated": "Paramétrage - Duplication",
  "selectFilter.setup_simulation_input_deleted": "Paramétrage - Suppression des entrées du modèle",
  "selectFilter.setup_simulation_input_replaced": "Paramétrage - Import des entrées du modèle",
  "selectFilter.setup_simulation_model_deleted": "Paramétrage - Suppression de modèle",
  "selectFilter.setup_simulation_model_replaced": "Paramétrage - Import de modèle",
  "selectFilter.setup_simulation_model_status_checked": "Paramétrage - Vérification du modèle",
  "selectFilter.setup_simulation_offset_changed": "Paramétrage - Modification des offsets",
  "selectFilter.setup_simulation_output_deleted": "Paramétrage - Suppression des sorties du modèle",
  "selectFilter.setup_simulation_output_replaced": "Paramétrage - Import des sorties du modèle",
  "selectFilter.simulation_ended": "Analyse - Simulation terminée",
  "selectFilter.snapshot_upserted": "Source - Import de données",
  "selectFilter.snapshots_deleted": "Source - Suppression de données",
  "selectFilter.source.created": "Source - Création de source",
  "selectFilter.source.deleted": "Source - Suppression de source",
  "selectFilter.source_duplicated": "Source - Duplication",
  "sensor_quantities_created.title"({
    authorName,
    count,
  }: {
    authorName: React.ReactNode;
    count: number;
  }) {
    return (
      <>
        {authorName} a ajouté {count} capteur{count > 1 ? "s" : ""}
      </>
    );
  },
  "sensor_value_deleted.title"({
    authorName,
    sensorTag,
    startPeriod,
    endPeriod,
    sourceLink,
  }: {
    authorName: React.ReactNode;
    sensorTag: string;
    startPeriod: string;
    endPeriod: string;
    sourceLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a supprimé les données du capteur {sensorTag} du {startPeriod} au {endPeriod}{" "}
        dans la source {sourceLink}
      </>
    );
  },
  "setupDiagnosisFailuresMetatada_changed.details.degradationType"({
    previousDegradationTypeName,
    currentDegradationTypeName,
  }: {
    previousDegradationTypeName: string | null;
    currentDegradationTypeName: string | null;
  }) {
    return `Modification du type de dégradation ${
      !currentDegradationTypeName && previousDegradationTypeName
        ? ` à un champ vide`
        : !previousDegradationTypeName && currentDegradationTypeName
          ? ` avec un ajout à ${currentDegradationTypeName}`
          : ` de ${previousDegradationTypeName} à ${currentDegradationTypeName}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.description"({
    previousDescription,
    currentDescription,
  }: {
    previousDescription: string | null;
    currentDescription: string | null;
  }) {
    return `Modification de la description ${
      !currentDescription && previousDescription
        ? ` à un champ vide`
        : !previousDescription && currentDescription
          ? ` avec un ajout à ${currentDescription}`
          : ` de ${previousDescription} à ${currentDescription}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.displayName"({
    previousDisplayName,
    currentDisplayName,
  }: {
    previousDisplayName: string;
    currentDisplayName: string;
  }) {
    return `Modification du nom d'affichage de ${previousDisplayName} à ${currentDisplayName}`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.equipmentType"({
    previousEquipmentTypeName,
    currentEquipmentTypeName,
  }: {
    previousEquipmentTypeName: string | null;
    currentEquipmentTypeName: string | null;
  }) {
    return `Modification du type d'équipement ${
      !currentEquipmentTypeName && previousEquipmentTypeName
        ? ` à un champ vide`
        : !previousEquipmentTypeName && currentEquipmentTypeName
          ? ` avec un ajout à ${currentEquipmentTypeName}`
          : ` de ${previousEquipmentTypeName} à ${currentEquipmentTypeName}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.equipments"({
    previousEquipments,
    currentEquipments,
  }: {
    previousEquipments: string[];
    currentEquipments: string[];
  }) {
    return `Modification des équipements ${
      currentEquipments.length === 0 && previousEquipments.length > 0
        ? ` à un champ vide`
        : previousEquipments.length === 0 && currentEquipments.length > 0
          ? ` avec un ajout à ${currentEquipments.join(",")}`
          : ` de ${previousEquipments.join(",")} à ${currentEquipments.join(",")}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.failureCategory"({
    previousFailureCategoryName,
    currentFailureCategoryName,
  }: {
    previousFailureCategoryName: string | null;
    currentFailureCategoryName: string | null;
  }) {
    return `Modification de la catégorie de défaillance ${
      !currentFailureCategoryName && previousFailureCategoryName
        ? ` à un champ vide`
        : !previousFailureCategoryName && currentFailureCategoryName
          ? ` avec un ajout à ${currentFailureCategoryName}`
          : ` de ${previousFailureCategoryName} à ${currentFailureCategoryName}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.name.modified"({
    currentName,
    previousName,
  }: {
    currentName: string;
    previousName: string;
  }) {
    return `Modification du nom de "${previousName}" à "${currentName}"`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.reparability"({
    previousReparability,
    currentReparability,
    translation,
  }: {
    previousReparability: Reparability | null;
    currentReparability: Reparability | null;
    translation: {
      FR: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
      EN: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
    };
  }) {
    return `Modification de la réparabilité ${
      currentReparability === null && previousReparability !== null
        ? ` à un champ vide`
        : previousReparability === null && currentReparability !== null
          ? ` avec un ajout à ${fr["reparability"](currentReparability, translation)}`
          : ` de ${fr["reparability"](previousReparability!, translation)} à ${fr["reparability"](
              currentReparability!,
              translation,
            )}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.suspectedElements"({
    previousSuspectedElements,
    currentSuspectedElements,
  }: {
    previousSuspectedElements: string[];
    currentSuspectedElements: string[];
  }) {
    return `Modification des éléments suspects ${
      currentSuspectedElements.length === 0 && previousSuspectedElements.length > 0
        ? ` à un champ vide`
        : previousSuspectedElements.length === 0 && currentSuspectedElements.length > 0
          ? ` avec un ajout à ${currentSuspectedElements.join(",")}`
          : ` de ${previousSuspectedElements.join(",")} à ${currentSuspectedElements.join(",")}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.system"({
    previousSystemName,
    currentSystemName,
  }: {
    previousSystemName: string | null;
    currentSystemName: string | null;
  }) {
    return `Modification du système ${
      !currentSystemName && previousSystemName
        ? ` à un champ vide`
        : !previousSystemName && currentSystemName
          ? ` avec un ajout à ${currentSystemName}`
          : ` de ${previousSystemName} à ${currentSystemName}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.unit"({
    previousUnit,
    currentUnit,
  }: {
    previousUnit: string | null;
    currentUnit: string | null;
  }) {
    return `Modification de l'unité ${
      !currentUnit && previousUnit
        ? ` à un champ vide`
        : !previousUnit && currentUnit
          ? ` avec un ajout à ${currentUnit}`
          : ` de ${previousUnit} à ${currentUnit}`
    }`;
  },
  "setup_changed.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a modifié les attributs du {setupLink}
      </>
    );
  },
  "setup_created.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a créé le {setupLink}
      </>
    );
  },
  "setup_deleted.title"({
    authorName,
    setupName,
  }: {
    authorName: React.ReactNode;
    setupName: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a supprimé le paramétrage {setupName}
      </>
    );
  },
  "setup_diagnosis_failures_deleted.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a supprimé les défaillances du {setupLink}
      </>
    );
  },
  "setup_diagnosis_failures_replaced.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a importé de nouvelles défaillances dans le {setupLink}
      </>
    );
  },
  "setup_diagnosis_parameters_changed.title"({
    authorName,
    setupLink,
    presetName,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
    presetName: string;
  }) {
    return (
      <>
        {authorName} a modifié le paramètrage de l&apos;algorithme {presetName} dans le {setupLink}
      </>
    );
  },
  "setup_diagnosis_preset_changed.title"({
    authorName,
    setupLink,
    prevPreset,
    currPreset,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
    prevPreset: string;
    currPreset: string;
  }) {
    return (
      <>
        {authorName} a modifié le type de l&apos;algorithme de {prevPreset} à {currPreset} dans le{" "}
        {setupLink}
      </>
    );
  },
  "setup_duplicated.title"({
    authorName,
    sourceSetupLink,
    targetSetupLink,
  }: {
    authorName: React.ReactNode;
    sourceSetupLink: React.ReactNode;
    targetSetupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a dupliqué le {sourceSetupLink} vers le {targetSetupLink}
      </>
    );
  },
  "setup_failures_changed.title"({
    authorName,
    setupLink,
    name,
    active,
  }: {
    authorName: React.ReactNode;
    active: boolean;
    setupLink: React.ReactNode;
    name: React.ReactNode;
  }) {
    return (
      <>
        {authorName} {active ? " a activé" : " a désactivé"} la défaillance {name} du {setupLink}
      </>
    );
  },
  "setup_failures_metadata_changed.title"({
    authorName,
    setupLink,
    name,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
    name: string;
  }) {
    return (
      <>
        {authorName} a modifié les métadonnées de la défaillance {name} dans le {setupLink}
      </>
    );
  },
  "setup_observable_changed.title"({
    authorName,
    setupLink,
    properties,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
    properties: ActivityIndexJSON["SETUP_DIAGNOSIS_OBSERVABLES_CHANGED"]["properties"];
  }) {
    return properties.current.active === undefined ? (
      <>
        {authorName} a modifié le sigma de l&apos;observable {properties.observableName} de{" "}
        {properties.previous.sigma} à {properties.current.sigma} dans le {setupLink}
      </>
    ) : (
      <>
        {authorName} a {properties.current.active ? "activé" : "désactivé"} l&apos;observable{" "}
        {properties.observableName} du {setupLink}
      </>
    );
  },
  "setup_observable_deleted.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a supprimé les observables du {setupLink}
      </>
    );
  },
  "setup_observable_replaced.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a importé de nouvelles observables dans le {setupLink}
      </>
    );
  },
  "setup_simulation_inputs.deleted.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a supprimé les entrées du modèle du {setupLink}
      </>
    );
  },
  "setup_simulation_inputs.replaced.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a importé de nouvelles entrées du modèle dans le {setupLink}
      </>
    );
  },
  "setup_simulation_model_deleted.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a supprimé le modèle du {setupLink}
      </>
    );
  },
  "setup_simulation_model_replaced.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a importé un nouveau modèle dans le {setupLink}
      </>
    );
  },
  "setup_simulation_model_status_checked.title"({
    setupLink,
    validated,
  }: {
    setupLink: React.ReactNode;
    validated: boolean;
  }) {
    return (
      <>
        Le paramétrage des simulations a été {validated ? "validé" : "rejeté"} pour le {setupLink}
      </>
    );
  },
  "setup_simulation_offset_changed.title"({
    authorName,
    setupLink,
    properties,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
    properties: ActivityIndexJSON["SETUP_SIMULATION_OFFSET_CHANGED"]["properties"];
  }) {
    return (
      <>
        {authorName} a modifié l’offset de la grandeur {properties.quantityName} de{" "}
        {properties.offsetOld} {properties.unit ? properties.unit : ""} à {properties.offsetNew}{" "}
        {properties.unit ? properties.unit : ""} dans le {setupLink}
      </>
    );
  },
  "setup_simulation_outputs.deleted.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a supprimé les sorties du modèle du {setupLink}
      </>
    );
  },
  "setup_simulation_outputs.replaced.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a importé de nouvelles sorties du modèle dans le {setupLink}
      </>
    );
  },
  "simulation_ended.title"({
    analysesLink,
    analysesCount,
  }: {
    analysesLink: React.ReactNode;
    analysesCount: number;
  }): React.ReactNode {
    if (analysesCount === 1) {
      return <>{analysesLink} simulation réalisée</>;
    }
    return <>{analysesLink} simulations réalisées</>;
  },
  "snapshots_deleted.title"({
    authorName,
    snapshotCount,
    sourceLink,
  }: {
    authorName: React.ReactNode;
    snapshotCount: number;
    sourceLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} a supprimé {snapshotCount}{" "}
        {`${snapshotCount > 1 ? "points de données" : "point de données"}`} dans la source{" "}
        {sourceLink}
      </>
    );
  },
  "source_deleted.title"({
    authorName,
    sourceName,
  }: {
    authorName: React.ReactNode;
    sourceName: string;
  }) {
    return (
      <>
        {authorName} a supprimé la source {sourceName}
      </>
    );
  },
  "source_upserted.title"({
    authorName,
    nbPoints,
    sourceLink,
    isSourceCreated,
  }: {
    authorName: React.ReactNode;
    nbPoints: number;
    sourceLink: React.ReactNode;
    isSourceCreated: boolean;
  }) {
    return (
      <>
        {authorName} a importé {nbPoints}{" "}
        {`${nbPoints > 1 ? "points de données" : "point de données"}`} dans la source {sourceLink}
        {isSourceCreated ? " (nouvelle source)" : ""}
      </>
    );
  },
  "unknown.title"({ authorName, action }: { authorName: React.ReactNode; action: string }) {
    return (
      <>
        {authorName} a effectué une action &quot;{action}&quot;
      </>
    );
  },
};
export default fr;
